import {Component, ElementRef, HostBinding, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild} from '@angular/core';
import {finalize, Subscription, timer} from "rxjs";

import {SaseungHanja} from '../../shared/models/saseung.model';
import {SASEUNG_ITEM_VIEW_INIT} from "../saseung-item/saseung-item.component";
import {PRELOAD_RATIO} from 'src/app/shared/defines/desktop.const';
import {ListLoader} from "../../shared/interfaces/list-loader";
import {SearchParam, UI_RELOAD_HEIGHT} from "../../shared/defines/client.const";
import {SaseungHanjaService} from "../../shared/services/saseung-hanja/saseung-hanja.service";
import {RedPipeService} from "../../shared/services/red-pipe/red-pipe.service";


@Component({
  selector: 'app-saseung-detail',
  templateUrl: './saseung-detail.component.html',
  styleUrls: ['./saseung-detail.component.scss']
})
export class SaseungDetailComponent implements OnDestroy, OnChanges {
  @HostBinding('attr.class') cssClass = 'schedule-list-box';
  @HostBinding('style.display') styleBlock = 'block';
  @ViewChild('scrollContainer') scrollContainer?: ElementRef;
  @ViewChild('reload') reload?: ElementRef;

  @Input() hangul: string | null = null;
  @Input() keyword: string | null = null;
  @Input() hun: string | null = null;
  @Input() hanja: string | null = null;
  // @Input() selected: string | null = null; // 선택되어서 해당 글자까지 스크롤 되어야 함.

  protected get dispTxt(): string | null {
    // keyword 검색만 있는 경우 list-tit-box를 표시하지 않음
    // hanja, hun, hangul이 있는 경우에만 표시
    if (this.keyword && !this.hanja && !this.hun && !this.hangul) {
      return null;
    }
    return this.hanja ?? this.hun ?? this.hangul ?? null;
  }

  // 화면 크기 조정
  private bodyHeight = UI_RELOAD_HEIGHT;

  // redPipe 처리
  private redPipe$: Subscription | null = null;

  protected isNoResult(): boolean {
    return (this.ll?.IsComplete() ?? false) && (this.ll?.getList()?.length == 0 ?? false);
  }

  // ListLoader 설정
  ll: ListLoader<SaseungHanja> | null = null;
  private getNextList$: Subscription | null = null;
  private needReload = false;

  constructor(
    private saseungHanjaSvc: SaseungHanjaService,
    private redPipeSvc: RedPipeService,
  ) {
    this.redPipe$ = this.redPipeSvc.Click$.subscribe((red) => {
      this.scrollContainer?.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
        // behavior: 'auto',
      });
    });
  }

  ngOnDestroy(): void {
    this.redPipe$?.unsubscribe();
    this.redPipe$ = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('hanja' in changes && changes['hanja'].currentValue) {
      this.hangul = null;
      this.keyword = null;
      this.hun = null;

      this.ll = this.saseungHanjaSvc;

      const param = {hanja: changes['hanja'].currentValue};
      this.ReloadList(param);
    }
    else if ('hun' in changes && changes['hun'].currentValue) {
      this.hangul = null;
      this.keyword = null;
      this.hanja = null;

      this.ll = this.saseungHanjaSvc;

      const param = {hun: changes['hun'].currentValue};
      this.ReloadList(param);
    }
    else if ('keyword' in changes && changes['keyword'].currentValue) {
      // keyword가 있으면 다른 파라미터 무시.
      this.hangul = null;
      this.hun = null;
      this.hanja = null;

      this.ll = this.saseungHanjaSvc;

      const param = {keyword: changes['keyword'].currentValue};
      this.ReloadList(param);
    }
    else if ('hangul' in changes && changes['hangul'].currentValue) {
      this.keyword = null;
      this.hun = null;
      this.hanja = null;

      this.ll = this.saseungHanjaSvc;

      const param = {hangul: changes['hangul'].currentValue};
      this.ReloadList(param);
    }
  }


  protected onItemViewInit(itemViewInit: SASEUNG_ITEM_VIEW_INIT): void {

    /**
     * 지정된 단어로 스크롤 되도록 하는 코드.
     * 지금은 사용할 일이 없어졌다.
     * TODO: 나중에 사용할 수도 있으니 주석처리만 한다.
     */
    // if (this.selected && !this.scrolled
    //   && itemViewInit.hanja?.hanja?.includes(this.selected)) {
    //   this.scrolled = true;
    //   itemViewInit.elementRef.nativeElement.scrollIntoView({behavior: 'smooth'});
    // }
  }

  private ReloadList(param: SearchParam): void {
    this.ll?.ResetList(param);
    this.loadList();
  }

  private loadList(): void {
    if (this.getNextList$) {
      console.warn('SaseungDetailComponent::loadList() - already call loadList()');
      return;
    }

    if (this.ll?.IsComplete()) {
      console.warn('SaseungDetailComponent::loadList() - already load all SaseungHanja');
      return;
    }

    if (this.ll) {
      this.getNextList$ = this.ll?.GetNextList().pipe(
        finalize(() => {
          this.getNextList$ = null;

          if (this.needReload) {
            timer(0).subscribe(() => {
              this.needReload = false;
              this.loadList();
            });
          }
        }),
      ).subscribe();
    }
  }

  /****************************************************************************
   * 일정 범위 이상 스크롤되면 남은 단어를 자동 로딩한다.
   ****************************************************************************/
  onScroll($event: Event): void {
    // console.log('SaseungComponent::onScroll()');

    if (this.scrollContainer === undefined) {
      throw new Error('SaseungDetailComponent::onScroll() - #scrollContainer  does not exist!!');
    }

    if (this.ll && !this.ll.IsLoading() && !this.ll.IsComplete() && this.reload) {
      const tar = $event.target || $event.srcElement || $event.currentTarget;
      const elContainer = this.scrollContainer.nativeElement;
      if (tar !== elContainer) {
        console.error('SaseungDetailComponent::onScroll() - wrong structure!!');
      }

      const elReload = this.reload.nativeElement;
      // console.log('onScroll() ', elReload.offsetTop, elReload.scrollTop, tar.offsetTop, tar.scrollTop, window.innerHeight);

      // console.warn('onScroll() ', elReload.offsetTop, elContainer.scrollTop, this.bodyHeight, elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight);
      if (elReload.offsetTop < elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight) {
        this.loadList();
      } else {
        // 스크롤 중에 이미 로딩이 진행되고 있으면, 로딩이 끝난 뒤에 다시 로딩이 되도록 요청. (비동기처리 순서 꼬임 방지)
        this.needReload = true;
      }
    }
  }
}
